import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { formatDate, statusTone } from "@/lib/format";
import { LogOut, FolderKanban, MessageSquareWarning, CheckCircle2, UploadCloud, Paperclip, FileCheck2, Eye } from "lucide-react";
import VerticalPhaseTracker from "@/components/app/VerticalPhaseTracker";
import SecureDocViewer from "@/components/app/SecureDocViewer";
import { ApprovalBadge } from "@/components/app/ClientApprovalPanel";
import RequestChangesDialog from "@/components/app/RequestChangesDialog";
import { usePushRegistration } from "@/hooks/usePushRegistration";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const clientApi = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: true });
clientApi.interceptors.request.use((c) => {
  const t = localStorage.getItem("client_token");
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

function ClientShell({ client, children }) {
  const nav = useNavigate();
  const logout = async () => {
    try { await clientApi.post("/client/logout"); } catch {}
    localStorage.removeItem("client_token"); nav("/client/login");
  };
  return (
    <div className="min-h-screen bg-cream">
      <header className="sticky top-0 z-40 bg-white border-b border-black/5">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/client" className="flex items-center gap-3 min-w-0" data-testid="client-portal-brand">
            <img src="/brand/logo.png" alt="Dr. Mariea's Publication and Research International Pvt. Ltd" className="h-10 w-auto select-none" draggable={false} />
            <span className="font-display text-sm leading-tight hidden sm:block" style={{ color: "#1B2A4A" }}>
              Dr. Mariea's Publication &amp; Research Intl. Pvt. Ltd
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="text-sm text-right">
              <div className="font-semibold" data-testid="client-name">{client?.name}</div>
              <div className="text-xs text-ink/60">{client?.company_name}</div>
            </div>
            <button data-testid="client-logout" onClick={logout} className="btn-outline flex items-center gap-2"><LogOut size={14} /> Sign out</button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-10">{children}</main>
    </div>
  );
}

function ConcernDialog({ open, onOpenChange, projects, defaultProjectId = "", onDone }) {
  const [form, setForm] = useState({ project_id: defaultProjectId, subject: "", body: "" });
  useEffect(() => { if (open) setForm({ project_id: defaultProjectId, subject: "", body: "" }); }, [open, defaultProjectId]);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.project_id) { toast.error("Please select the project this concern is about"); return; }
    try {
      await clientApi.post("/client/concerns", form);
      toast.success("Concern raised. Your manager has been notified.");
      onOpenChange(false); onDone && onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-[1.25rem] max-w-md" data-testid="concern-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Raise a concern</DialogTitle>
          <DialogDescription>Your studio manager will be notified and respond via the portal.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><label className="label-caps block mb-1">Project</label>
            <select data-testid="concern-project" required value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}
              className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">— Select a project —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.project_number ? `${p.project_number} · ` : ""}{p.name}</option>)}
            </select></div>
          <div><label className="label-caps block mb-1">Subject</label>
            <Input data-testid="concern-subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-lg border-black/15" /></div>
          <div><label className="label-caps block mb-1">Details</label>
            <Textarea data-testid="concern-body" required rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="rounded-lg border-black/15" /></div>
          <button data-testid="concern-submit" type="submit" className="btn-primary w-full">Submit concern</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ClientApprovalSection({ projectId }) {
  const [info, setInfo] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const load = () => clientApi.get(`/client/projects/${projectId}/approval`).then((r) => setInfo(r.data)).catch(() => setInfo(null));
  useEffect(() => { load(); }, [projectId]);
  if (!info) return null;
  const approved = info.status === "Client Approved";
  const approve = async () => {
    if (!window.confirm("Approve this project? Your studio team will be notified.")) return;
    setBusy(true);
    try { await clientApi.post(`/client/projects/${projectId}/approve`); toast.success("Project approved. Thank you!"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Approval failed"); }
    setBusy(false);
  };
  return (
    <div className={`mt-8 bg-white rounded-[1.25rem] p-6 border-2 ${approved ? "border-emerald-500" : "border-gold"}`} data-testid="client-approval-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="label-caps flex items-center gap-2"><FileCheck2 size={12} /> Client Approval</div>
        <ApprovalBadge status={info.status} testid="client-approval-status" />
      </div>
      <p className="text-sm text-ink/70 mt-2">Review the draft files below. They open in a protected, view-only viewer.</p>
      <ul className="mt-4 space-y-2" data-testid="client-approval-files">
        {info.files.map((f) => (
          <li key={f.id} className="flex items-center justify-between border border-black/5 rounded-lg px-4 py-3">
            <div className="min-w-0">
              <div className="font-semibold truncate">{f.name}</div>
              <div className="text-xs text-ink/50 font-mono-tight">{formatDate(f.uploaded_at)} · {f.page_count} page{f.page_count === 1 ? "" : "s"}</div>
            </div>
            <button data-testid={`client-view-file-${f.id}`} onClick={() => setViewing(f)} className="inline-flex items-center gap-1 text-royal-700 underline underline-offset-4 text-sm"><Eye size={14} /> View</button>
          </li>
        ))}
        {info.files.length === 0 && <li className="text-sm text-ink/60 text-center py-4">No files shared for approval yet.</li>}
      </ul>
      {approved ? (
        <div className="mt-4 text-sm text-emerald-700 font-semibold inline-flex items-center gap-2" data-testid="client-approved-note">
          <CheckCircle2 size={16} /> Approved on {new Date(info.approved_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
        </div>
      ) : info.files.length > 0 && (
        <>
          {info.status === "Changes Requested" && info.last_change_request && (
            <div className="mt-4 rounded-lg px-4 py-3 text-sm bg-orange-50 border border-orange-200 text-orange-900" data-testid="client-last-change-request">
              <div className="font-semibold">You requested changes on {info.last_change_request.file_name}</div>
              <div className="mt-1 whitespace-pre-wrap">{info.last_change_request.comment}</div>
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-3">
            <button data-testid="client-request-changes-btn" onClick={() => setChangesOpen(true)} className="border-2 border-amber-500 text-amber-700 px-5 py-2 rounded-full font-semibold text-sm inline-flex items-center gap-2">
              <MessageSquareWarning size={16} /> Request changes
            </button>
            <button data-testid="client-approve-btn" disabled={busy} onClick={approve} className="bg-gold text-ink px-5 py-2 rounded-full font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60">
              <CheckCircle2 size={16} /> {busy ? "Approving…" : "Approve project"}
            </button>
          </div>
        </>
      )}
      <RequestChangesDialog open={changesOpen} onOpenChange={setChangesOpen} api={clientApi} projectId={projectId} files={info.files} onDone={load} />
      {viewing && <SecureDocViewer client={clientApi} file={viewing} onClose={() => setViewing(null)}
        pageUrl={(n) => `/client/projects/${projectId}/approval-files/${viewing.id}/pages/${n}`} />}
    </div>
  );
}

export default function ClientPortal() {
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [concernOpen, setConcernOpen] = useState(false);
  const nav = useNavigate();
  usePushRegistration({
    swPath: "/trac-sw.js",
    publicKeyUrl: `${BACKEND_URL}/api/client/push/public-key`,
    subscribeUrl: "/client/push/subscribe",
    post: (url, body) => clientApi.post(url, body),
    enabled: !!client,
  });

  useEffect(() => {
    clientApi.get("/client/me").then((r) => setClient(r.data)).catch(() => nav("/client/login"));
    clientApi.get("/client/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, [nav]);

  if (!client) return <div className="p-8 text-neutral-500 text-sm">Loading…</div>;

  return (
    <ClientShell client={client}>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
        <div className="label-caps">Your projects</div>
        <h1 className="font-display text-4xl sm:text-5xl text-royal-800 mt-2 break-words">Hi, {client.name.split(" ")[0]}.</h1>
        <p className="text-ink/70 mt-2">Track progress across every engagement. Updates are posted by your studio managers.</p>
        </div>
        {projects.length > 0 && <button data-testid="dashboard-raise-concern-btn" onClick={() => setConcernOpen(true)} className="btn-primary inline-flex items-center gap-2"><MessageSquareWarning size={14} /> Raise a concern</button>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="client-projects-grid">
        {projects.map((p) => (
          <Link to={`/client/projects/${p.id}`} key={p.id} data-testid={`client-project-${p.id}`} className="bg-white border border-black/5 rounded-[1.25rem] p-6 card-lift">
            <div className="flex items-start justify-between gap-3">
              <div className="font-display text-2xl text-royal-800 min-w-0 break-words [overflow-wrap:anywhere]">{p.name}</div>
              <span className={`shrink-0 px-2 py-0.5 text-xs font-mono-tight uppercase rounded ${statusTone(p.status)}`}>{p.status}</span>
            </div>
            <div className="text-sm text-ink/60 mt-1">{p.client_name}</div>
            <div className="grid grid-cols-3 gap-3 mt-6">
              <div><div className="label-caps">Tasks</div><div className="font-display text-2xl">{p.work_order_count}</div></div>
              <div><div className="label-caps">Done</div><div className="font-display text-2xl text-royal-700">{p.completed_count}</div></div>
              <div><div className="label-caps">Due</div><div className="font-mono-tight text-sm">{formatDate(p.due_date)}</div></div>
            </div>
            {p.client_approval_status && <div className="mt-4"><ApprovalBadge status={p.client_approval_status} testid={`client-dash-approval-${p.id}`} /></div>}
          </Link>
        ))}
        {projects.length === 0 && <div className="col-span-2 text-center py-16 text-ink/60">No projects yet. Your studio manager will assign you soon.</div>}
      </div>
      <ConcernDialog open={concernOpen} onOpenChange={setConcernOpen} projects={projects} />
    </ClientShell>
  );
}

export function ClientProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [data, setData] = useState(null);
  const [concernOpen, setConcernOpen] = useState(false);
  const [allProjects, setAllProjects] = useState([]);
  const [concerns, setConcerns] = useState([]);
  const [clientFiles, setClientFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const loadFiles = () => {
    clientApi.get(`/client/projects/${id}/files`).then((r) => setClientFiles(r.data)).catch(() => setClientFiles([]));
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await clientApi.post(`/client/projects/${id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} shared with your studio team`);
      loadFiles();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };

  const load = () => {
    clientApi.get(`/client/projects/${id}`).then((r) => setData(r.data)).catch(() => nav("/client"));
    clientApi.get("/client/concerns").then((r) => setConcerns(r.data.filter((c) => c.project_id === id))).catch(() => {});
  };  useEffect(() => {
    clientApi.get("/client/me").then((r) => setClient(r.data)).catch(() => nav("/client/login"));
    clientApi.get("/client/projects").then((r) => setAllProjects(r.data)).catch(() => {});
    load();
    loadFiles();
  }, [id]);

  if (!client || !data) return <div className="p-8 text-neutral-500 text-sm">Loading…</div>;
  const { project, work_orders, updates } = data;

  return (
    <ClientShell client={client}>
      <Link to="/client" className="text-sm underline underline-offset-4">← All projects</Link>
      <div className="mt-4 flex flex-wrap justify-between items-start gap-3">
        <div>
          <div className="label-caps">Project</div>
          <h1 className="font-display text-3xl sm:text-5xl text-royal-800 mt-1 break-words [overflow-wrap:anywhere]">{project.name}</h1>
          <div className="text-ink/60 mt-1">{project.client_name} · due {formatDate(project.due_date)}</div>
        </div>
        <button data-testid="raise-concern-btn" onClick={() => setConcernOpen(true)} className="btn-primary inline-flex items-center gap-2"><MessageSquareWarning size={14} /> Raise a concern</button>
      </div>

      <div className="mt-6">
        <VerticalPhaseTracker phases={project.phases || []} projectStatus={project.status} testid="client-phase-tracker" />
      </div>

      <ClientApprovalSection projectId={id} />

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-black/5 rounded-[1.25rem] p-6 lg:col-span-2">
          <div className="label-caps flex items-center gap-2"><FolderKanban size={12} /> Tasks</div>
          <ul className="mt-4 space-y-3" data-testid="client-work-orders">
            {work_orders.map((w) => (
              <li key={w.id} className="flex items-center justify-between border border-black/5 rounded-lg px-4 py-3">
                <div>
                  <div className="font-semibold">{w.title}</div>
                  <div className="text-xs text-ink/60 font-mono-tight">Due {w.deadline || "—"}</div>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`px-2 py-0.5 text-xs font-mono-tight uppercase rounded ${statusTone(w.status)}`}>{w.status}</span>
                </div>
              </li>
            ))}
            {work_orders.length === 0 && <li className="text-sm text-ink/60 text-center py-6">No tasks yet.</li>}
          </ul>
        </div>

        <div className="bg-royal-800 text-cream rounded-[1.25rem] p-6">
          <div className="label-caps text-cream/60">Updates from your studio</div>
          <div className="mt-4 space-y-3 max-h-96 overflow-auto" data-testid="client-updates">
            {updates.length === 0 && <div className="text-cream/60 text-sm">No updates posted yet.</div>}
            {updates.map((u) => (
              <div key={u.id} className="border-l-2 border-gold pl-3">
                <div className="text-xs text-cream/60 font-mono-tight">{u.user_name} · {formatDate(u.created_at)}</div>
                <div className="text-sm mt-1 whitespace-pre-wrap">{u.body}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-black/5 rounded-[1.25rem] p-6 lg:col-span-3" data-testid="client-attachments-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="label-caps flex items-center gap-2"><Paperclip size={12} /> Attachments shared with the studio</div>
            <label className="btn-primary cursor-pointer inline-flex items-center gap-2 text-sm" data-testid="client-upload-label">
              <UploadCloud size={14} />
              {uploading ? "Uploading…" : "Attach file"}
              <input
                type="file"
                data-testid="client-upload-input"
                disabled={uploading}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ""; }}
                className="hidden"
              />
            </label>
          </div>
          <p className="text-xs text-ink/60 mt-2">Files you share here are instantly visible to your Super Admin, Operation Head and assigned Manager (up to 20 MB per file).</p>
          <ul className="mt-4 space-y-2" data-testid="client-attachments-list">
            {clientFiles.map((f) => (
              <li key={f.id} data-testid={`client-attachment-${f.id}`} className="flex items-center justify-between border border-black/5 rounded-lg px-4 py-2 text-sm">
                <div className="min-w-0 truncate">
                  <span className="font-semibold truncate">{f.name}</span>
                  <span className="text-xs text-ink/50 font-mono-tight ml-2">{f.size ? `${Math.round(f.size / 1024)} KB` : ""}</span>
                </div>
                <div className="text-xs text-ink/50 font-mono-tight">{formatDate(f.uploaded_at)}</div>
              </li>
            ))}
            {clientFiles.length === 0 && <li className="text-sm text-ink/60 text-center py-4">No files shared yet.</li>}
          </ul>
        </div>

        <div className="bg-white border border-black/5 rounded-[1.25rem] p-6 lg:col-span-3">
          <div className="label-caps">Your concerns</div>
          <ul className="mt-4 space-y-3" data-testid="client-concerns">
            {concerns.map((c) => (
              <li key={c.id} className="border border-black/5 rounded-lg p-3">
                <div className="flex justify-between">
                  <div className="font-semibold"><span className="font-mono-tight text-xs text-ink/50 mr-2">{c.ticket_no || ""}</span>{c.subject}</div>
                  <span className={`px-2 py-0.5 text-xs font-mono-tight uppercase rounded ${statusTone(c.status)}`}>{c.status}</span>
                </div>
                <div className="text-sm text-ink/70 mt-1 whitespace-pre-wrap">{c.body}</div>
                <div className="text-xs text-ink/50 font-mono-tight mt-1">{formatDate(c.created_at)}</div>
                {c.response && (
                  <div className="mt-3 border-l-2 border-royal-700 pl-3 bg-cream-2/60 rounded-r-lg py-2" data-testid={`portal-concern-response-${c.id}`}>
                    <div className="text-xs text-royal-800 font-mono-tight uppercase tracking-widest">{c.responded_by || "Studio"} replied · {c.responded_at ? formatDate(c.responded_at) : ""}</div>
                    <div className="text-sm text-ink mt-1 whitespace-pre-wrap">{c.response}</div>
                  </div>
                )}
              </li>
            ))}
            {concerns.length === 0 && <li className="text-sm text-ink/60">No concerns raised on this project.</li>}
          </ul>
        </div>
      </div>

      <ConcernDialog open={concernOpen} onOpenChange={setConcernOpen} projects={allProjects.length ? allProjects : [project]} defaultProjectId={id} onDone={load} />
    </ClientShell>
  );
}
